// Plant location tab (admin): coordinates used for weather.
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState, useEffect } from 'react';
import { useApi, putJson } from '../../api.js';

// ============================================================================
//  PlantLocationPanel -- lat/lon + address search via geocoding.
//  Admin-only. Stored in kv_store namespace 'plant-settings'.
// ============================================================================
export default function PlantLocationPanel() {
  // Data: saved plant coordinates (used for the Daily Ops weather) + form fields.
  const { data, loading, error, refresh } = useApi('/api/plant-settings');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [addr, setAddr] = useState('');
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (data && data.settings) {
      setLat(data.settings.lat != null ? String(data.settings.lat) : '');
      setLon(data.settings.lon != null ? String(data.settings.lon) : '');
      setAddr(data.settings.address || '');
    }
  }, [data]);

  // Geocode the address via Nominatim (OpenStreetMap's free lookup).
  // No key required. Rate-limited to 1 request/sec, which is fine for
  // this admin panel.
  async function lookupAddress() {
    const q = addr.trim();
    if (!q) { setMsg({ kind: 'error', text: 'Type an address first.' }); return; }
    setLooking(true); setMsg(null);
    try {
      const url = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(q);
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('Nominatim HTTP ' + res.status);
      const arr = await res.json();
      if (!Array.isArray(arr) || arr.length === 0) {
        throw new Error('No match for "' + q + '". Try a broader address (city + street).');
      }
      const hit = arr[0];
      const nLat = Number(hit.lat), nLon = Number(hit.lon);
      if (!Number.isFinite(nLat) || !Number.isFinite(nLon)) throw new Error('Bad coords in Nominatim response.');
      setLat(nLat.toFixed(6));
      setLon(nLon.toFixed(6));
      setMsg({ kind: 'ok', text: 'Found: ' + (hit.display_name || q) + '. Click Save to keep.' });
    } catch (e) { setMsg({ kind: 'error', text: e.message || String(e) }); }
    finally { setLooking(false); }
  }

  // Save coordinates.
  async function save() {
    setSaving(true); setMsg(null);
    try {
      const latN = Number(lat), lonN = Number(lon);
      if (!Number.isFinite(latN) || latN < -90 || latN > 90) throw new Error('Latitude must be between -90 and 90.');
      if (!Number.isFinite(lonN) || lonN < -180 || lonN > 180) throw new Error('Longitude must be between -180 and 180.');
      if (Math.abs(latN) < 0.0001 && Math.abs(lonN) < 0.0001) {
        throw new Error('0, 0 is not a valid plant location. Click "Look up from address" first.');
      }
      await putJson('/api/plant-settings', { lat: latN, lon: lonN, address: addr.trim() || null });
      setMsg({ kind: 'ok', text: 'Saved. Weather auto-fill will use this location.' });
      refresh();
    } catch (e) { setMsg({ kind: 'error', text: e.message || String(e) }); }
    finally { setSaving(false); }
  }

  if (loading && !data) return <div className="loading">Loading plant settings...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const envLat = data && data.envDefault && data.envDefault.lat;
  const envLon = data && data.envDefault && data.envDefault.lon;

  // Screen: address lookup, latitude / longitude, Save.
  return (
    <div style={{ maxWidth: 520 }}>
      <p className="small" style={{ color: '#64748b' }}>
        Weather auto-fill uses this latitude/longitude. Falls back to env vars (
        {envLat != null ? envLat.toFixed(4) : '-'}, {envLon != null ? envLon.toFixed(4) : '-'}) when unset.
      </p>
      <div style={{ display: 'grid', gap: '0.6rem' }}>
        <label className="small">Address / description
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <input type="text" value={addr} onChange={e => setAddr(e.target.value)}
                   placeholder="60A Baywood Road, Etobicoke, ON"
                   style={{ flex: 1 }} />
            <button className="refresh-btn" type="button" onClick={lookupAddress} disabled={looking}
                    style={{ background: '#7c3aed', flexShrink: 0, padding: '4px 12px' }}
                    title="Look up latitude / longitude via OpenStreetMap">
              {looking ? 'Looking...' : 'Look up'}
            </button>
          </div>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
          <label className="small">Latitude
            <input type="number" step="0.0001" value={lat} onChange={e => setLat(e.target.value)}
                   placeholder="43.6532" style={{ width: '100%' }} />
          </label>
          <label className="small">Longitude
            <input type="number" step="0.0001" value={lon} onChange={e => setLon(e.target.value)}
                   placeholder="-79.3832" style={{ width: '100%' }} />
          </label>
        </div>
        {msg && (
          <div className="small" style={{ padding: '0.35rem 0.5rem', borderRadius: 4,
                                          background: msg.kind === 'ok' ? '#dcfce7' : '#fee2e2',
                                          color:      msg.kind === 'ok' ? '#166534' : '#991b1b' }}>
            {msg.text}
          </div>
        )}
        <div>
          <button className="refresh-btn" onClick={save} disabled={saving}
                  style={{ background: '#0369a1' }}>
            {saving ? 'Saving...' : 'Save plant location'}
          </button>
          <a className="small"
             style={{ marginLeft: '0.75rem', color: '#0369a1' }}
             href={lat && lon ? `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}` : '#'}
             target="_blank" rel="noreferrer">
            View on map
          </a>
        </div>
      </div>
    </div>
  );
}
